import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import vizIcon from "../assets/logos/vw1.png";
import vIcon from "../assets/logos/vizwalk-mark-white.png";
import indiaFlag from "../assets/icons/flag-in.png";
import usFlag from "../assets/icons/flag-us.png";
import alertIcon from "../assets/icons/warning.png";
import "./Navbar.css";
import { supabase } from "../lib/supabaseClient";
import { useIsAdmin } from "../features/admin";

const TOPBAR_KEY = "vwTopBarClosed";

// Flip to false to hide the VR tab without touching the router.
const VR_TAB_ENABLED = false

function getInitialShowTopBar() {
  return sessionStorage.getItem(TOPBAR_KEY) !== "1";
}

/* ---------- REGION HELPERS ---------- */
function normalizeRegion(v = "") {
  const s = String(v || "").trim().toLowerCase();
  if (s === "us") return "us";
  return "in";
}

function getRegionFromPath(pathname = "") {
  const parts = pathname.split("/").filter(Boolean);
  const first = (parts[0] || "").toLowerCase();

  if (first === "in" || first === "us") return first;
  return "in";
}

function stripRegionFromPath(pathname = "") {
  const parts = pathname.split("/").filter(Boolean);
  const first = (parts[0] || "").toLowerCase();

  if (first === "in" || first === "us") {
    return "/" + parts.slice(1).join("/");
  }
  return pathname || "/";
}

function buildRegionalPath(region, pathname = "/", search = "", hash = "") {
  const cleanRegion = normalizeRegion(region);
  let basePath = stripRegionFromPath(pathname);

  if (!basePath || basePath === "") basePath = "/";
  if (!basePath.startsWith("/")) basePath = `/${basePath}`;

  const regionalPath =
    basePath === "/"
      ? `/${cleanRegion}`
      : `/${cleanRegion}${basePath}`;

  return `${regionalPath}${search || ""}${hash || ""}`;
}

export default function Navbar({
  user,
  signOut,
  selectedServer = "india",
  setSelectedServer,
}) {
  const [dd, setDd] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileProjectsOpen, setMobileProjectsOpen] = useState(false);
  const [showTopBar, setShowTopBar] = useState(getInitialShowTopBar);
  const [warningData, setWarningData] = useState({
    warningStatus: "",
    warningText: "",
  });
  const ddRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useIsAdmin();

  const currentRegion = getRegionFromPath(location.pathname);
  const effectiveServer = currentRegion === "us" ? "us" : "india";

  useEffect(() => {
    if (setSelectedServer) {
      setSelectedServer(effectiveServer);
    }
  }, [effectiveServer, setSelectedServer]);

  useEffect(() => {
    const onDown = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) setDd(false);
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        const btn = document.querySelector(".vwHamburger");
        if (btn && !btn.contains(e.target)) {
          setMobileMenuOpen(false);
          setMobileProjectsOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
  const handleBeforeUnload = () => {
    sessionStorage.removeItem(TOPBAR_KEY);
  };

  window.addEventListener("beforeunload", handleBeforeUnload);

  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
}, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileProjectsOpen(false);
    setDd(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    let alive = true;
    supabase.from("app_settings").select("key,value").then(({ data, error }) => {
      if (!alive || error || !data) return;
      const s = Object.fromEntries(data.map((r) => [r.key, r.value || {}]));
      setWarningData({
        warningStatus: s.server_warning?.active ? "active" : "",
        warningText: s.server_warning?.text || "",
      });
    });
    return () => { alive = false; };
  }, []);

  const pathname = stripRegionFromPath(location.pathname || "/");
  const hash = location.hash || "";

  const isHomeActive =
    pathname === "/" && (hash === "" || hash === "#featured-projects");

  const isProjectsActive =
    pathname === "/live-projects" ||
    pathname === "/showcase" ||
    (pathname === "/" && hash === "#featured-projects");

  const isDemoActive = pathname.startsWith("/discover");
  const isVrActive = pathname.startsWith("/vr");

  const openLearn = () =>
    window.open(
      buildRegionalPath(currentRegion, "/learn"),
      "_blank",
      "noopener,noreferrer"
    );

  const goToRegionPath = (targetPath) => {
    const next = buildRegionalPath(currentRegion, targetPath, "", "");
    navigate(next);
  };

  const goHome = () => {
    setDd(false);
    setMobileMenuOpen(false);
    setMobileProjectsOpen(false);
    navigate(buildRegionalPath(currentRegion, "/", "", ""));
  };

  const changeRegion = (server) => {
    const nextRegion = server === "us" ? "us" : "in";

    if (setSelectedServer) {
      setSelectedServer(server);
    }

    const nextUrl = buildRegionalPath(
      nextRegion,
      location.pathname,
      location.search,
      location.hash
    );

    localStorage.setItem("vw_region", nextRegion); // "in" or "us"

    navigate(nextUrl);
  };

  const isWarningActive =
    String(warningData.warningStatus || "").trim().toLowerCase() === "active" &&
    String(warningData.warningText || "").trim() !== "";

  return (
    <>
      <div className="vwNavWrap">
        {showTopBar && isWarningActive && (
          <div className="vwTopBar vwTopBarAlert">
            <div className="vwContainer vwTopBarInner">
              <div className="vwTopBarTextWrap">
                <img
                  src={alertIcon}
                  alt="Alert"
                  className="vwTopBarAlertIcon"
                />
                <div className="vwTopBarText">
                  {String(warningData.warningText || "").trim()}
                </div>
              </div>

              <button
                type="button"
                className="vwTopBarClose"
                onClick={() => {
                  sessionStorage.setItem(TOPBAR_KEY, "1");
                  setShowTopBar(false);
                }}
                aria-label="Close"
              >
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M1 1L13 13M1 13L13 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="vwNav">
          <div className="vwContainer vwNavInner">
            <a
              className="vwLogoGroup"
              href="/"
              onClick={(e) => {
                e.preventDefault();
                goHome();
              }}
            >
              <img className="vwLogoIcon" src={vizIcon} alt="Vizwalk Logo" />
              <div className="vwLogoDivider" />
              <img className="vwIcon" src={vIcon} alt="Vizwalk Logo" />
            </a>

            <div className="vwNavLinks">
              <button
                className={`vwNavLink ${isHomeActive ? "isActive" : ""}`}
                onClick={() => {
                  setDd(false);
                  goToRegionPath("/");
                }}
              >
                HOME
              </button>

              <div className="vwDd" ref={ddRef}>
                <button
                  className={`vwNavLink ${isProjectsActive ? "isActive" : ""}`}
                  onClick={() => setDd((v) => !v)}
                >
                  PROJECTS ▾
                </button>

                {dd && (
                  <div className="vwDdMenu">
                    <button
                      className="vwDdItem"
                      onClick={() => {
                        setDd(false);
                        goToRegionPath("/showcase");
                      }}
                    >
                      Showcase Projects
                    </button>

                    <button
                      className="vwDdItem"
                      onClick={() => {
                        setDd(false);
                        goToRegionPath("/live-projects");
                      }}
                    >
                      Live Projects
                    </button>
                  </div>
                )}
              </div>

              {VR_TAB_ENABLED && (
                <button
                  className={`vwNavLink ${isVrActive ? "isActive" : ""}`}
                  onClick={() => {
                    setDd(false);
                    goToRegionPath("/vr");
                  }}
                >
                  VR
                </button>
              )}

              <button
                className={`vwNavLink ${isDemoActive ? "isActive" : ""}`}
                onClick={() => {
                  setDd(false);
                  goToRegionPath("/discover");
                }}
              >
                DISCOVER
              </button>
            </div>

            <div className="vwNavRight">
              <div className="vwRegionContainer">
                <button
                  className={`vwRegionBtn ${
                    effectiveServer === "india" ? "active" : ""
                  }`}
                  onClick={() => changeRegion("india")}
                  type="button"
                >
                  <img src={indiaFlag} alt="IN" className="vwFlagIcon" />
                  IN
                </button>

                <button
                  className={`vwRegionBtn ${
                    effectiveServer === "us" ? "active" : ""
                  }`}
                  onClick={() => changeRegion("us")}
                  type="button"
                >
                  <img src={usFlag} alt="US" className="vwFlagIcon" />
                  US
                </button>
              </div>

              <button
                className="vwIconBtn"
                title="Learn"
                aria-label="Learn"
                onClick={openLearn}
                type="button"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 10L12 5 2 10l10 5 10-5z" />
                  <path d="M6 12v5c0 1 2.7 3 6 3s6-2 6-3v-5" />
                  <path d="M22 10v6" />
                </svg>
              </button>

              {isAdmin === true && (
                <button
                  className="vwIconBtn"
                  title="Admin panel"
                  aria-label="Admin panel"
                  onClick={() => navigate("/admin")}
                  type="button"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>
              )}

              <button
                className="vwIconBtn"
                title={user?.email || "Logout"}
                onClick={signOut}
                type="button"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </button>

              <button
                type="button"
                className={`vwHamburger ${mobileMenuOpen ? "isOpen" : ""}`}
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((v) => !v)}
              >
                <span />
                <span />
                <span />
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="vwMobileMenu" ref={mobileMenuRef}>
              <button
                className={`vwMobileLink ${isHomeActive ? "isActive" : ""}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  goToRegionPath("/");
                }}
              >
                HOME
              </button>

              <div className="vwMobileProjectsWrap">
                <button
                  className={`vwMobileLink ${isProjectsActive ? "isActive" : ""}`}
                  onClick={() => setMobileProjectsOpen((v) => !v)}
                >
                  <span>PROJECTS</span>
                  <span className={`vwMobileCaret ${mobileProjectsOpen ? "isOpen" : ""}`}>
                    ▾
                  </span>
                </button>

                {mobileProjectsOpen && (
                  <div className="vwMobileSubmenu">
                    <button
                      className="vwMobileSubLink"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileProjectsOpen(false);
                        goToRegionPath("/showcase");
                      }}
                    >
                      Showcase Projects
                    </button>

                    <button
                      className="vwMobileSubLink"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileProjectsOpen(false);
                        goToRegionPath("/live-projects");
                      }}
                    >
                      Live Projects
                    </button>
                  </div>
                )}
              </div>

              {VR_TAB_ENABLED && (
                <button
                  className={`vwMobileLink ${isVrActive ? "isActive" : ""}`}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    goToRegionPath("/vr");
                  }}
                >
                  VR
                </button>
              )}

              <button
                className={`vwMobileLink ${isDemoActive ? "isActive" : ""}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  goToRegionPath("/discover");
                }}
              >
                DISCOVER
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className="vwHeaderSpacer"
        style={{ height: showTopBar && isWarningActive ? "124px" : "76px" }}
      />
    </>
  );
}